import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useCouponStore } from '@/store/couponStore';
import { Button } from '@/components/ui/button';
import { FaCircleArrowLeft } from "react-icons/fa6";
import { RiCoupon3Fill, RiFileCopyLine } from 'react-icons/ri';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';

export function Coupons() {
  const { t, currentLang } = useI18n();
  const navigate = useNavigate();
  const { coupons, fetchCoupons, isLoading } = useCouponStore();

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const activeCoupons = coupons.filter(c => {
    const now = new Date();
    const isActive = c.is_active;
    const isNotExpired = !c.end_date || new Date(c.end_date) > now;
    const isStarted = !c.start_date || new Date(c.start_date) <= now;
    const hasUsageLeft = !c.usage_limit || c.usage_count < c.usage_limit;
    return isActive && isNotExpired && isStarted && hasUsageLeft;
  });

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success(t('success.copied') || 'Copied to clipboard');
  };

  return (
    <div className="min-h-[100dvh] bg-gray-50/50 font-sans pb-32 lg:pb-10">
      {/* Premium Header — Gold Standard */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100/50 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl hover:bg-gray-50 text-black transition-all active:scale-95"
            onClick={() => navigate(-1)}
          >
            <FaCircleArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-lg font-black tracking-tight text-center flex-1">
            {currentLang === 'bn' ? 'কুপনসমূহ' : 'Coupons'}
          </h1>
          <div className="w-10" /> {/* Spacer */}
        </div>
      </header>

      <div className="container mx-auto px-4 max-w-lg mt-6">
        {isLoading && activeCoupons.length === 0 ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-28 bg-gray-200 rounded-[24px] animate-pulse" />
            ))}
          </div>
        ) : activeCoupons.length > 0 ? (
          <div className="space-y-4">
            <AnimatePresence>
                {activeCoupons.map((coupon, index) => (
                <motion.div
                    key={coupon.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden relative group"
                >
                    {/* Voucher Ticket Cutouts */}
                    <div className="absolute top-1/2 -left-3 w-6 h-6 bg-gray-50/80 rounded-full border border-gray-100 -translate-y-1/2 z-10" />
                    <div className="absolute top-1/2 -right-3 w-6 h-6 bg-gray-50/80 rounded-full border border-gray-100 -translate-y-1/2 z-10" />
                    
                    <div className="flex">
                    {/* Left: Discount Info */}
                    <div className="w-[100px] p-4 bg-gradient-to-br from-primary to-primary/80 text-white flex flex-col items-center justify-center border-r border-dashed border-white/40 relative">
                        <span className="text-3xl font-black leading-none tracking-tighter">
                        {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `৳${coupon.discount_value}`}
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] mt-2 opacity-95">OFF</span>
                    </div>

                    {/* Right: Coupon Details */}
                    <div className="flex-1 p-5 pl-7 flex flex-col justify-between">
                        <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <Badge variant="outline" className="text-primary border-primary/20 bg-primary/5 font-black uppercase text-[10px] tracking-widest px-2 py-0.5 rounded-lg">
                            {coupon.code}
                            </Badge>
                            {coupon.end_date && (
                            <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">
                                {currentLang === 'bn' ? 'মেয়াদ:' : 'Exp:'} {new Date(coupon.end_date).toLocaleDateString()}
                            </span>
                            )}
                        </div>
                        <h3 className="font-bold text-black text-sm leading-tight">
                            {currentLang === 'bn' ? 'স্পেশাল ডিসকাউন্ট অফার' : 'Special Discount Offer'}
                        </h3>
                        <p className="text-[11px] text-gray-500 mt-1.5 font-medium">
                            {currentLang === 'bn' 
                            ? `সর্বনিম্ন অর্ডারে ৳${coupon.min_order_amount} ডিসকাউন্ট প্রযোজ্য` 
                            : `Valid on orders over ৳${coupon.min_order_amount}`}
                        </p>
                        </div>
                        
                        <div className="mt-4 flex items-center justify-between">
                        <span className="text-[10px] text-primary/70 font-black uppercase tracking-widest">
                            {coupon.usage_limit ? (currentLang === 'bn' ? `${coupon.usage_count}/${coupon.usage_limit} ব্যবহৃত` : `${coupon.usage_count}/${coupon.usage_limit} Used`) : (currentLang === 'bn' ? 'আনলিমিটেড ব্যবহার' : 'Unlimited Use')}
                        </span>
                        <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-8 px-4 rounded-xl bg-primary/5 hover:bg-primary/10 text-primary font-bold text-xs gap-2 transition-all active:scale-95"
                            onClick={() => handleCopyCode(coupon.code)}
                        >
                            <RiFileCopyLine className="w-3.5 h-3.5" />
                            {currentLang === 'bn' ? 'কপি' : 'Copy'}
                        </Button>
                        </div>
                    </div>
                    </div>
                </motion.div>
                ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-10 text-center border-2 border-dashed border-gray-100 shadow-sm mt-10">
            <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <RiCoupon3Fill className="w-10 h-10 text-primary/40" />
            </div>
            <h3 className="text-xl font-bold text-black mb-2">
              {currentLang === 'bn' ? 'কোনো কুপন নেই' : 'No Coupons Available'}
            </h3>
            <p className="text-sm text-gray-500 max-w-[240px] mx-auto">
              {currentLang === 'bn' 
                ? 'বর্তমানে আপনার অ্যাকাউন্টে কোনো অ্যাক্টিভ কুপন নেই। নতুন অফার এলে এখানে দেখতে পাবেন।' 
                : 'You currently have no active coupons. Check back later for special offers and discounts.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
